import { useCallback, useRef, useEffect } from 'react';
import { Node, Edge } from 'reactflow';

interface HistoryState {
  nodes: Node[];
  edges: Edge[];
}

interface UseFlowHistoryProps {
  nodes: Node[];
  edges: Edge[];
  setNodes: (nodes: Node[] | ((nodes: Node[]) => Node[])) => void;
  setEdges: (edges: Edge[] | ((edges: Edge[]) => Edge[])) => void;
  maxHistorySize?: number;
}

export function useFlowHistory({
  nodes,
  edges,
  setNodes,
  setEdges,
  maxHistorySize = 50,
}: UseFlowHistoryProps) {
  const historyRef = useRef<HistoryState[]>([]);
  const currentIndexRef = useRef<number>(-1);
  const isUndoRedoRef = useRef<boolean>(false);
  const lastStateRef = useRef<string>('');

  // Save state to history
  const saveToHistory = useCallback(() => {
    if (isUndoRedoRef.current) {
      isUndoRedoRef.current = false;
      return;
    }

    const currentState: HistoryState = {
      nodes: JSON.parse(JSON.stringify(nodes)),
      edges: JSON.parse(JSON.stringify(edges)),
    };

    const stateString = JSON.stringify(currentState);
    
    // Don't save if state hasn't changed
    if (stateString === lastStateRef.current) {
      return;
    }
    
    lastStateRef.current = stateString;

    // Remove any future states if we're not at the end
    if (currentIndexRef.current < historyRef.current.length - 1) {
      historyRef.current = historyRef.current.slice(0, currentIndexRef.current + 1);
    }

    // Add new state
    historyRef.current.push(currentState);
    currentIndexRef.current = historyRef.current.length - 1;

    // Trim history if it exceeds max size
    if (historyRef.current.length > maxHistorySize) {
      historyRef.current = historyRef.current.slice(-maxHistorySize);
      currentIndexRef.current = historyRef.current.length - 1;
    }
  }, [nodes, edges, maxHistorySize]);

  // Debounced save to history (saves after 300ms of no changes)
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      saveToHistory();
    }, 300);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [nodes, edges, saveToHistory]);

  // Undo
  const undo = useCallback(() => {
    if (currentIndexRef.current <= 0) {
      return false;
    }

    isUndoRedoRef.current = true;
    currentIndexRef.current--;
    const previousState = historyRef.current[currentIndexRef.current];
    
    if (previousState) {
      lastStateRef.current = JSON.stringify(previousState);
      setNodes(previousState.nodes);
      setEdges(previousState.edges);
      return true;
    }
    
    return false;
  }, [setNodes, setEdges]);

  // Redo
  const redo = useCallback(() => {
    if (currentIndexRef.current >= historyRef.current.length - 1) {
      return false;
    }

    isUndoRedoRef.current = true;
    currentIndexRef.current++;
    const nextState = historyRef.current[currentIndexRef.current];
    
    if (nextState) {
      lastStateRef.current = JSON.stringify(nextState);
      setNodes(nextState.nodes);
      setEdges(nextState.edges);
      return true;
    }
    
    return false;
  }, [setNodes, setEdges]);

  // Check if undo/redo is available
  const canUndo = currentIndexRef.current > 0;
  const canRedo = currentIndexRef.current < historyRef.current.length - 1;

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if we're in an input field
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      // Ctrl+Z or Cmd+Z for undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }

      // Ctrl+Shift+Z or Cmd+Shift+Z for redo
      // Also Ctrl+Y for redo on Windows
      if (
        ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) ||
        ((e.ctrlKey || e.metaKey) && e.key === 'y')
      ) {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  return {
    undo,
    redo,
    canUndo,
    canRedo,
    saveToHistory,
  };
}
